/**
 * t187u: the web side of the offline clock (docs/offline.md §3.4). Clock in / out and breaks go straight to the server
 * when there's signal and nothing is waiting; with no signal (or when the send hits a network error / 5xx), the punch
 * is saved in the user's IndexedDB outbox (`hub-<userId>` / `outbox`) with the tap time and the location fix, shown
 * at once, and sent in order when the connection is back (same triggers as the run outbox, lib/runOutbox).
 */
import { useMemo, useSyncExternalStore } from 'react';
import { clockOutboxStore, createClockOutbox, toPendingClockOp, type ClockOpPayload, type ClockOutboxOp } from '@tradeoutlet/data';
import { ApiError, CLOCK_ACTION_OP, CLOCK_OP_LABEL, classifyOutcome, type ClockAction, type PendingClockOp, type TimeEntry } from '@tradeoutlet/shared';
import { api } from './api';
import { hubDbName, indexedDbStore } from './idbStore';
import { queryClient } from './queryClient';
import { useUi } from './store';
import { connection } from './connection';
import { uuid } from './uuid';

export const clockOutbox = createClockOutbox({
  store: clockOutboxStore(userId => indexedDbStore(hubDbName(userId))),
  uuid,
  isOnline: () => connection.isDeviceOnline(), // t574u: navigator.onLine, unless the API has answered since
  // The server's entry replaces the optimistic one (or, after a Discard, the server's state comes back).
  onSent: op => { void queryClient.invalidateQueries({ queryKey: ['wf', op.workspaceId] }); },
});

export function useClockOps(): ClockOutboxOp[] {
  return useSyncExternalStore(clockOutbox.subscribe, clockOutbox.ops, clockOutbox.ops);
}
/** This workspace's waiting punches, for applyPendingClock. */
export function usePendingClock(ws: string | null | undefined): PendingClockOp[] {
  const ops = useClockOps();
  return useMemo(() => ops.filter(o => o.workspaceId === ws).map(toPendingClockOp), [ops, ws]);
}

/** The queued clock-in (not refused) that later punches belong to, if any. */
export const pendingClockIn = (ws: string) => clockOutbox.ops().find(o => o.workspaceId === ws && o.kind === 'clock.in' && o.state !== 'failed' && o.state !== 'conflict') ?? null;

/**
 * One clock tap. `payload.device_time` is the moment of the tap (taken before waiting for a location fix). Online with
 * nothing waiting: sent now, recorded at the server's time (an enforced fence can still refuse a clock-in). Otherwise,
 * or when the send can't get through, it's queued and the answer is `{ queued: true }`. Refusals throw as before.
 */
export async function submitClock(ws: string, action: ClockAction, payload: ClockOpPayload): Promise<{ queued: true } | { queued: false; entry: TimeEntry }> {
  const kind = CLOCK_ACTION_OP[action];
  const waiting = clockOutbox.ops().some(o => o.workspaceId === ws && o.state !== 'failed' && o.state !== 'conflict');
  if (connection.isDeviceOnline() && !waiting) {
    const key = uuid();
    const p = payload;
    try {
      const entry = kind === 'clock.in'
        ? await api.wf.clockIn(ws, { client_event_id: p.client_event_id, shift_id: p.shift_id ?? undefined, location_id: p.shift_id ? undefined : p.location_id ?? undefined, geo: p.geo ?? null }, key)
        : kind === 'clock.out' ? await api.wf.clockOut(ws, { client_event_id: p.client_event_id, geo: p.geo ?? null }, key)
          : kind === 'break.start' ? await api.wf.startBreak(ws, { client_event_id: p.client_event_id }, key) : await api.wf.endBreak(ws, { client_event_id: p.client_event_id }, key);
      void queryClient.invalidateQueries({ queryKey: ['wf', ws] });
      return { queued: false, entry };
    } catch (e) {
      // A refusal is the answer; only "couldn't get through" falls back to the outbox (same client_event_id, so a send
      // that did land and lost its answer can't punch twice).
      if (classifyOutcome(e instanceof ApiError ? e.status : null) !== 'retry') throw e;
    }
  }
  await clockOutbox.enqueue({ kind, workspaceId: ws, payload });
  return { queued: true };
}

// A punch the server refused: say so once, with the fix (the offline bar turns red until it's dealt with).
const told = new Set<string>();
clockOutbox.subscribe(() => {
  for (const o of clockOutbox.ops()) {
    if ((o.state === 'failed' || o.state === 'conflict') && !told.has(o.id)) {
      told.add(o.id);
      useUi.getState().toast(`${CLOCK_OP_LABEL[o.kind]} not sent: ${o.error ?? 'refused'}. ${o.fix ?? ''}`.trim(), 'error');
    }
    if (o.state === 'queued') told.delete(o.id);
  }
});
